import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  Tooltip,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'

interface BusinessReview {
  id: string
  undertaker_id: string | null
  chain_id: string | null
  source: string
  author: string | null
  rating: number
  text: string | null
  review_date: string | null
  hidden: boolean
  featured: boolean
  language: string | null
  business_name: string
}

export default function BusinessReviews() {
  const { isAdmin } = useAuth()
  const [reviews, setReviews] = useState<BusinessReview[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [businessFilter, setBusinessFilter] = useState<string>('all')

  useEffect(() => {
    async function fetchReviews() {
      setLoading(true)
      setError(null)
      try {
        // ponytail: fetch-all in 1000-row pages and filter client-side;
        // switch to server-side filtering per business if this grows past ~10k rows
        const all: any[] = []
        const pageSize = 1000
        for (let from = 0; ; from += pageSize) {
          const { data, error } = await supabase
            .from('business_reviews')
            .select('*, undertaker:undertakers(name), chain:chains(name)')
            .order('review_date', { ascending: false })
            .range(from, from + pageSize - 1)
          if (error) throw error
          all.push(...(data || []))
          if (!data || data.length < pageSize) break
        }
        setReviews(all.map((r) => ({
          ...r,
          business_name: r.undertaker?.name || r.chain?.name || 'Ukendt',
        })))
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchReviews()
  }, [])

  const businesses = useMemo(() => {
    const map = new Map<string, { key: string; name: string; count: number }>()
    reviews.forEach((r) => {
      const key = r.undertaker_id ? `u:${r.undertaker_id}` : `c:${r.chain_id}`
      const existing = map.get(key)
      if (existing) existing.count++
      else map.set(key, { key, name: r.business_name, count: 1 })
    })
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'da'))
  }, [reviews])

  const visibleReviews = useMemo(() => {
    if (businessFilter === 'all') return reviews
    return reviews.filter((r) =>
      (r.undertaker_id ? `u:${r.undertaker_id}` : `c:${r.chain_id}`) === businessFilter
    )
  }, [reviews, businessFilter])

  const toggleFlag = async (review: BusinessReview, flag: 'hidden' | 'featured') => {
    const newValue = !review[flag]
    // Optimistic update, revert on error
    setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, [flag]: newValue } : r)))
    const { error } = await supabase
      .from('business_reviews')
      .update({ [flag]: newValue })
      .eq('id', review.id)
    if (error) {
      setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, [flag]: !newValue } : r)))
      setError(`Kunne ikke opdatere: ${error.message}`)
    }
  }

  if (!isAdmin) return null

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const hiddenCount = visibleReviews.filter((r) => r.hidden).length
  const featuredCount = visibleReviews.filter((r) => r.featured).length

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Google Anmeldelser
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Anmeldelser hentes automatisk fra Google. Skjulte anmeldelser vises aldrig på sitet,
        fremhævede prioriteres i citat-sektionen. Kun anmeldelser på dansk vises på sitet.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Forretning</InputLabel>
          <Select
            value={businessFilter}
            label="Forretning"
            onChange={(e) => setBusinessFilter(e.target.value)}
          >
            <MenuItem value="all">Alle forretninger ({reviews.length} anmeldelser)</MenuItem>
            {businesses.map((b) => (
              <MenuItem key={b.key} value={b.key}>
                {b.name} ({b.count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {visibleReviews.length} anmeldelser · {hiddenCount} skjulte · {featuredCount} fremhævede
        </Typography>
      </Box>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Forretning</TableCell>
                <TableCell>Dato</TableCell>
                <TableCell>Forfatter</TableCell>
                <TableCell align="center">Rating</TableCell>
                <TableCell sx={{ minWidth: 280 }}>Anmeldelse</TableCell>
                <TableCell align="center">Sprog</TableCell>
                <TableCell align="center">Fremhævet</TableCell>
                <TableCell align="center">Skjult</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleReviews.map((review) => (
                <TableRow
                  key={review.id}
                  hover
                  sx={{ opacity: review.hidden ? 0.45 : 1 }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {review.business_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {review.review_date
                      ? new Date(review.review_date).toLocaleDateString('da-DK', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })
                      : '–'}
                  </TableCell>
                  <TableCell>{review.author || '–'}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                      <StarIcon sx={{ fontSize: 16, color: '#fbbc04' }} />
                      <Typography variant="body2" fontWeight={600}>
                        {Number(review.rating).toFixed(0)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={review.text || ''} placement="top-start">
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {review.text || <em>Ingen tekst</em>}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={review.language === 'da' ? '' : 'Vises ikke på sitet – kun danske anmeldelser synkroniseres'}
                    >
                      <Chip
                        label={review.language || '?'}
                        size="small"
                        color={review.language === 'da' ? 'default' : 'warning'}
                        sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      color="success"
                      checked={review.featured}
                      onChange={() => toggleFlag(review, 'featured')}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      color="error"
                      checked={review.hidden}
                      onChange={() => toggleFlag(review, 'hidden')}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {visibleReviews.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary">Ingen anmeldelser fundet</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  )
}
